// @ts-check
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { HiOutlineFilter } from 'react-icons/hi';
import { Popover, PopoverHandler, PopoverContent } from '@material-tailwind/react';

import { TestableButton, TestableCheckbox, TestableText } from '@components/general/TestableComponents';
import { countSelected, isSelected, toggleValue } from './filterUtils';

/**
 * Material Tailwind's PopoverContent props come from an explicit `Pick` over
 * `React.ComponentProps<'div'>`, which against the installed @types/react makes optional
 * DOM props required - so every usage is reported as missing `placeholder`, `onResize` and
 * three others, whatever is passed. A library type fault, not a props fault. The cast
 * can be dropped once Material Tailwind fixes it.
 */
const PopoverPanel = /** @type {React.ComponentType<any>} */ (/** @type {unknown} */ (PopoverContent));

/**
 * A filter button and the panel it opens: one titled section per group, a checkbox per
 * option, Reset / Done beneath.
 *
 * @param {Object} props
 * @param {import('./filterUtils').FilterGroup[]} props.groups
 * @param {import('./filterUtils').FilterSelection} props.selection
 * @param {(selection: import('./filterUtils').FilterSelection) => void} props.onChange
 * @param {string} [props.label]
 * @param {string} [props.dataTestId]
 * @returns {JSX.Element}
 */
const FilterPopover = ({ groups = [], selection = {}, onChange, label = 'Filter', dataTestId = 'filter' }) => {
  const [open, setOpen] = useState(false);
  const activeCount = countSelected(selection);

  return (
    <Popover open={open} handler={setOpen} placement="bottom-start" offset={8}>
      <PopoverHandler>
        <TestableButton
          dataTestId={`${dataTestId}-button`}
          variant="outlined"
          className="flex items-center gap-2 normal-case"
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <HiOutlineFilter className="h-4 w-4" />
          {label}
          {/*
            The sr-only text is part of the button's accessible name: "Filter 6" alone
            does not say what the 6 counts.
          */}
          {activeCount > 0 && (
            <span
              data-testid={`${dataTestId}-count`}
              className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs font-semibold text-white"
            >
              {activeCount}
              <span className="sr-only"> filters applied</span>
            </span>
          )}
        </TestableButton>
      </PopoverHandler>

      <PopoverPanel
        data-testid={`${dataTestId}-panel`}
        role="dialog"
        aria-label={`${label} options`}
        className="z-50 w-64 p-0"
      >
        <div className="max-h-96 overflow-y-auto p-4 flex flex-col">
          {groups.map((group) => (
            <fieldset key={group.key} className="mb-4 last:mb-0 flex flex-col">
              <legend className="mb-1">
                <TestableText
                  dataTestId={`${dataTestId}-group-${group.key}`}
                  className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                >
                  {group.label}
                </TestableText>
              </legend>

              {group.options.map((option) => (
                <TestableCheckbox
                  key={option.value}
                  dataTestId={`${dataTestId}-option-${group.key}-${option.value}`}
                  id={`${dataTestId}-${group.key}-${option.value}`}
                  label={option.label}
                  checked={isSelected(selection, group.key, option.value)}
                  onChange={() => onChange?.(toggleValue(selection, group.key, option.value))}
                  ripple={false}
                  className="checked:border-blue-500 checked:bg-blue-500"
                  containerProps={{ className: 'p-2' }}
                  labelProps={{ className: 'text-gray-900 dark:text-gray-100' }}
                />
              ))}
            </fieldset>
          ))}

          {groups.length === 0 && (
            <TestableText dataTestId={`${dataTestId}-empty`} className="text-gray-700 dark:text-gray-300">
              There is nothing to filter on yet.
            </TestableText>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-300 px-4 py-3 dark:border-gray-700">
          {/*
            Left enabled with nothing selected - a disabled control the reader cannot
            explain is worse than one that harmlessly does nothing - but aria says so.
          */}
          <TestableButton
            dataTestId={`${dataTestId}-reset`}
            variant="text"
            className="normal-case"
            onClick={() => onChange?.({})}
            aria-disabled={activeCount === 0}
          >
            Reset
          </TestableButton>

          <TestableButton
            dataTestId={`${dataTestId}-done`}
            variant="text"
            className="normal-case"
            onClick={() => setOpen(false)}
          >
            Done
          </TestableButton>
        </div>
      </PopoverPanel>
    </Popover>
  );
};

FilterPopover.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ).isRequired,
      getValues: PropTypes.func,
      chipPrefix: PropTypes.string,
    })
  ),
  selection: PropTypes.object,
  onChange: PropTypes.func,
  label: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default FilterPopover;